/**
 * useCeleryTask — polling hook per task Celery.
 *
 * Uso:
 *   const { taskData, callLog, running, start, stop, reset } = useCeleryTask()
 *
 *   // Dopo enqueue:
 *   start(id_task)
 *
 *   // Opzionale: callback al completamento
 *   const { start } = useCeleryTask({ onDone: (data) => ... })
 */
import { ref, onUnmounted } from 'vue'
import { api } from '../services/api.js'

const POLL_INTERVAL_MS = 2000
const POLL_MAX         = 150   // 5 minuti max
const TERMINAL_STATES  = ['SUCCESS', 'FAILURE', 'REVOKED']

export function useCeleryTask({ onDone } = {}) {
  const taskData = ref(null)
  const callLog  = ref([])
  const running  = ref(false)

  let pollTimer = null
  let pollCount = 0

  function _log(label, ok, msg) {
    callLog.value.unshift({ label, ok, msg })
    if (callLog.value.length > 20) callLog.value.pop()
  }

  function start(id_task) {
    stop()
    running.value = true
    pollCount = 0
    taskData.value = { id_task, status: 'PENDING', progress: 0 }

    pollTimer = setInterval(async () => {
      pollCount++

      if (pollCount > POLL_MAX) {
        stop()
        _log(`POLL #${pollCount}`, false, `Stop: superato limite ${POLL_MAX} tentativi`)
        return
      }

      const res = await api.get(`/api/v2/tasks/task/${id_task}`)

      if (!res.ok) {
        _log(`POLL #${pollCount}`, false, `${res.status ?? ''} — ${res.message ?? 'errore'}`)
        return
      }

      _log(`POLL #${pollCount}`, true, `status=${res.data.status} progress=${res.data.progress ?? 0}%`)
      taskData.value = res.data

      if (TERMINAL_STATES.includes(res.data.status)) {
        stop()
        onDone?.(res.data)
      }
    }, POLL_INTERVAL_MS)
  }

  function stop() {
    if (pollTimer) { clearInterval(pollTimer); pollTimer = null }
    running.value = false
  }

  function reset() {
    stop()
    taskData.value = null
    callLog.value  = []
  }

  onUnmounted(stop)

  return { taskData, callLog, running, start, stop, reset }
}
